import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import BarcodeInput from "../components/BarcodeInput";
import CameraScanner from "../components/CameraScanner";
import LabelPhotoScanner from "../components/LabelPhotoScanner";
import { useAuth } from "../lib/auth";
import { parseBarcodeLabel } from "../lib/barcodeLabel";
import { toast } from "sonner";
import {
  Camera,
  ScanSearch,
  Trash2,
  Check,
  Plus,
  ArrowDownToLine,
  Lock,
  X,
} from "lucide-react";

/**
 * Toplu Giriş
 * - Kullanıcı sehpa konumu + tarih + açıklama girer
 * - Barkod okutur. Barkod DB'de varsa adet artırılır, yoksa "yeni ürün" formu açılır.
 * - Tüm liste tek tıkla kaydedilir.
 */
export default function BulkEntry() {
  const { user } = useAuth();
  const isAdmin = ["admin", "supervisor"].includes(user?.role);
  const isSupervisor = user?.role === "supervisor";
  const [sehpa, setSehpa] = useState("");
  const [tarih, setTarih] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [aciklama, setAciklama] = useState("");
  const [rows, setRows] = useState([]); // {barkod, adet, isim, ebat, aciklama, urun_kalitesi, isNew, currentAdet}
  const [cameraOpen, setCameraOpen] = useState(false);
  const [newFormFor, setNewFormFor] = useState(null); // barcode string awaiting new-product details
  const [newForm, setNewForm] = useState({
    isim: "",
    ebat: "",
    aciklama: "",
    urun_kalitesi: "SAĞLAM",
    adet: 1,
  });
  const [submitting, setSubmitting] = useState(false);
  const [shelves, setShelves] = useState([]);
  const [manualBarkod, setManualBarkod] = useState("");
  const [manualAdet, setManualAdet] = useState(1);
  const [lastScan, setLastScan] = useState(null); // {barkod, isim, ebat, adet, sehpa_konumu, status: 'found'|'new'|'incremented'}
  const [labelScannerOpen, setLabelScannerOpen] = useState(false);

  useEffect(() => {
    api.get("/shelves").then(({ data }) => setShelves(data)).catch(() => {});
  }, []);

  const handleScan = useCallback(
    async (raw, adetToAdd = 1) => {
      const parsed = parseBarcodeLabel(raw);
      const barkod = String(parsed.barkod || "").trim();
      const adetInc = Math.max(1, Number(adetToAdd) || 1);
      if (!barkod) return { ok: false };
      if (!sehpa.trim()) {
        toast.error("Önce SEHPA KONUMU girin.");
        return { ok: false };
      }

      // If the label carried extra fields, propagate the geliş tarihi to the
      // fiş header (once, only if user hasn't set it explicitly).
      if (parsed.gelis_tarihi && tarih === new Date().toISOString().slice(0, 10)) {
        setTarih(parsed.gelis_tarihi);
      }

      // if already in the list, just increment
      const existingIdx = rows.findIndex((r) => r.barkod === barkod);
      if (existingIdx >= 0) {
        const target = rows[existingIdx];
        setRows((prev) =>
          prev.map((r, i) =>
            i === existingIdx ? { ...r, adet: r.adet + adetInc } : r,
          ),
        );
        setLastScan({
          barkod,
          isim: target.isim,
          ebat: target.ebat,
          adet: target.adet + adetInc,
          currentAdet: target.currentAdet,
          sehpa_konumu: sehpa,
          status: "incremented",
        });
        toast.success(`+${adetInc} · ${barkod}`);
        return { ok: true };
      }

      // check DB
      try {
        const { data } = await api.get(`/inventory/by-barcode/${encodeURIComponent(barkod)}`);
        if (data.pending_entry) {
          toast.error(
            `Bu cam giriş onayına gönderildi, tekrar giriş yapamazsınız · ${barkod} (talep eden: ${data.pending_entry.requester})`,
            { duration: 7000 },
          );
          return { ok: false };
        }
        if (data.found) {
          setRows((prev) => [
            {
              barkod,
              adet: adetInc,
              isim: data.item.isim,
              ebat: data.item.ebat,
              aciklama: data.item.aciklama,
              urun_kalitesi: data.item.urun_kalitesi,
              isNew: false,
              currentAdet: data.item.adet,
            },
            ...prev,
          ]);
          setLastScan({
            barkod,
            isim: data.item.isim,
            ebat: data.item.ebat,
            adet: adetInc,
            currentAdet: data.item.adet,
            sehpa_konumu: data.item.sehpa_konumu,
            status: "found",
          });
          toast.success(`Bulundu: ${data.item.isim || barkod}`);
          return { ok: true };
        } else {
          // open new-product form pre-filled from label metadata if present
          setNewFormFor(barkod);
          setNewForm({
            isim: parsed.isim || "",
            ebat: parsed.ebat || "",
            aciklama: parsed.aciklama || "",
            urun_kalitesi: "SAĞLAM",
            adet: adetInc,
          });
          setLastScan({
            barkod,
            isim: parsed.isim || "",
            ebat: parsed.ebat || "",
            adet: adetInc,
            currentAdet: 0,
            sehpa_konumu: "",
            status: "new",
          });
          return { ok: true };
        }
      } catch (e) {
        toast.error(formatApiError(e));
        return { ok: false };
      }
    },
    [rows, sehpa, tarih],
  );

  const submitManual = async () => {
    const b = manualBarkod.trim();
    if (!b) {
      toast.error("Barkod boş olamaz.");
      return;
    }
    const res = await handleScan(b, manualAdet);
    if (res?.ok) {
      setManualBarkod("");
      setManualAdet(1);
    }
  };

  // Camera-OCR of the full label. Convert the returned parsed fields into the
  // delimited format that handleScan already understands, then invoke it.
  const handleLabelExtract = async (extracted) => {
    if (!extracted?.barkod) {
      toast.error("Etiketten barkod alınamadı.");
      return;
    }
    if (!sehpa.trim()) {
      toast.error("Önce SEHPA KONUMU girin.");
      return;
    }
    const parts = [
      extracted.barkod,
      extracted.isim || "",
      extracted.ebat || "",
      extracted.gelis_tarihi || "",
    ];
    const composed = parts.join("|");
    await handleScan(composed, extracted.adet || 1);
  };

  const addNewProduct = () => {
    if (!newForm.isim.trim()) {
      toast.error("İsim boş olamaz.");
      return;
    }
    setRows((prev) => [
      {
        barkod: newFormFor,
        adet: Number(newForm.adet) || 1,
        isim: newForm.isim.trim(),
        ebat: newForm.ebat.trim(),
        aciklama: newForm.aciklama.trim(),
        urun_kalitesi: newForm.urun_kalitesi,
        isNew: true,
      },
      ...prev,
    ]);
    setNewFormFor(null);
    toast.success(`Yeni ürün eklendi: ${newForm.isim}`);
  };

  const removeRow = (barkod) =>
    setRows((prev) => prev.filter((r) => r.barkod !== barkod));

  const changeAdet = (barkod, delta) =>
    setRows((prev) =>
      prev.map((r) =>
        r.barkod === barkod
          ? { ...r, adet: Math.max(1, r.adet + delta) }
          : r,
      ),
    );

  const changeEbat = (barkod, ebat) =>
    setRows((prev) =>
      prev.map((r) => (r.barkod === barkod ? { ...r, ebat } : r)),
    );

  const changeIsim = (barkod, isim) =>
    setRows((prev) =>
      prev.map((r) => (r.barkod === barkod ? { ...r, isim } : r)),
    );

  const submit = async () => {
    if (!sehpa.trim()) {
      toast.error("SEHPA KONUMU zorunlu.");
      return;
    }
    if (rows.length === 0) {
      toast.error("En az bir ürün ekleyin.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        sehpa_konumu: sehpa.trim().toUpperCase(),
        gelis_tarihi: tarih,
        aciklama: aciklama,
        items: rows.map((r) => ({
          barkod: r.barkod,
          adet: r.adet,
          isim: r.isim,
          ebat: r.ebat,
          aciklama: r.aciklama,
          urun_kalitesi: r.urun_kalitesi,
        })),
      };
      const { data } = await api.post(
        isSupervisor ? "/entries/bulk" : "/approvals/entry",
        body,
      );
      if (isSupervisor) {
        toast.success(`Fiş #${data.fis_id} · ${rows.length} kayıt işlendi`);
      } else {
        toast.success(
          `Giriş talebi oluşturuldu · ${data.count} kayıt Supervisor onayı bekliyor`,
        );
      }
      setRows([]);
      setAciklama("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const totalAdet = rows.reduce((a, r) => a + Number(r.adet || 0), 0);

  return (
    <div className="space-y-6">
      {!isAdmin && (
        <div
          data-testid="bulk-entry-nonadmin-notice"
          className="hard border border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] p-3 text-sm flex items-start gap-2"
        >
          <Lock size={14} className="mt-0.5 shrink-0" />
          <span>
            Mevcut bir camın <b>ismini</b> yalnızca yönetici değiştirebilir.
            Ebat ve adet gibi diğer alanlar sizin için serbesttir.
          </span>
        </div>
      )}
      {/* Header form */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5">
        <div className="ticker text-[color:var(--text-3)] mb-4">
          /// GİRİŞ FİŞİ · SEHPAYA CAM EKLE
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Sehpa Konumu <span className="text-[color:var(--error)]">*</span>
            </label>
            <input
              data-testid="bulk-entry-sehpa"
              list="shelves-list"
              value={sehpa}
              onChange={(e) => setSehpa(e.target.value.toUpperCase())}
              placeholder="Örn: A-1, AX-14"
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none font-mono font-bold"
            />
            <datalist id="shelves-list">
              {shelves.map((s) => (
                <option key={s.sehpa} value={s.sehpa} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Geliş Tarihi
            </label>
            <input
              data-testid="bulk-entry-date"
              type="date"
              value={tarih}
              onChange={(e) => setTarih(e.target.value)}
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
            />
          </div>
          <div>
            <label className="ticker block mb-2 text-[color:var(--text-2)]">
              Açıklama
            </label>
            <input
              data-testid="bulk-entry-note"
              value={aciklama}
              onChange={(e) => setAciklama(e.target.value)}
              placeholder="Fiş açıklaması (opsiyonel)"
              className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
            />
          </div>
        </div>
      </div>

      {/* Scanner */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div className="ticker text-[color:var(--text-3)]">
            /// BARKOD OKUT
          </div>
          <div className="flex gap-2">
            <button
              data-testid="bulk-entry-label-scanner-btn"
              onClick={() => setLabelScannerOpen(true)}
              className="flex items-center gap-2 px-3 py-2 hard border-2 border-[color:var(--border)] hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] transition-colors text-sm font-semibold"
              title="Fotoğraf çek — etiketin tamamını oku"
            >
              <ScanSearch size={16} /> Etiket OCR
            </button>
            <button
              data-testid="bulk-entry-camera-btn"
              onClick={() => setCameraOpen(true)}
              className="flex items-center gap-2 px-3 py-2 hard border-2 border-[color:var(--border)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors text-sm font-semibold"
            >
              <Camera size={16} /> Kameradan Okut
            </button>
          </div>
        </div>
        <BarcodeInput
          onScan={handleScan}
          disabled={!sehpa.trim() || submitting}
          placeholder={
            !sehpa.trim()
              ? "Önce sehpa konumu girin…"
              : "Barkodu okutun veya yazıp Enter'a basın"
          }
          testId="bulk-entry-barcode-input"
        />
        <div className="text-xs text-[color:var(--text-3)]">
          USB barkod okuyucu otomatik çalışır. Alan her okumadan sonra
          kendiliğinden temizlenir ve tekrar odaklanır.
        </div>
      </div>

      {/* Son okunan cam — büyük, prominent */}
      {lastScan && (
        <div
          data-testid="bulk-entry-last-scan"
          className={`hard border-2 p-5 row-enter ${
            lastScan.status === "new"
              ? "border-[color:var(--accent)] bg-[color:var(--accent-bg)]"
              : "border-[color:var(--primary)] bg-white"
          }`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="ticker text-[color:var(--text-3)]">
              /// SON OKUNAN
              {lastScan.status === "found" && (
                <span className="ml-2 text-[color:var(--success)]">✓ TANINDI</span>
              )}
              {lastScan.status === "incremented" && (
                <span className="ml-2 text-[color:var(--primary)]">+ ADET ARTIRILDI</span>
              )}
              {lastScan.status === "new" && (
                <span className="ml-2 text-[color:var(--accent)]">⚠ YENİ ÜRÜN</span>
              )}
            </div>
            <button
              onClick={() => setLastScan(null)}
              className="p-1 hard hover:bg-[color:var(--bg)] text-[color:var(--text-3)]"
              aria-label="Kapat"
            >
              <X size={14} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 items-center">
            <div className="md:col-span-2">
              <div className="ticker text-[10px] text-[color:var(--text-3)]">
                İsim
              </div>
              <div
                data-testid="last-scan-isim"
                className="text-xl md:text-2xl font-black tracking-tight break-words"
              >
                {lastScan.isim || "-"}
              </div>
            </div>
            <div>
              <div className="ticker text-[10px] text-[color:var(--text-3)]">
                Barkod
              </div>
              <div className="font-mono font-bold text-sm truncate">
                {lastScan.barkod}
              </div>
            </div>
            <div>
              <div className="ticker text-[10px] text-[color:var(--text-3)]">
                Ebat
              </div>
              <div className="font-mono font-bold text-sm">
                {lastScan.ebat || "-"}
              </div>
            </div>
            <div>
              <div className="ticker text-[10px] text-[color:var(--text-3)]">
                Mevcut → Yeni
              </div>
              <div className="font-mono font-bold text-sm">
                {lastScan.status === "new"
                  ? `+ ${lastScan.adet}`
                  : `${lastScan.currentAdet} → ${lastScan.currentAdet + lastScan.adet}`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual entry */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-5">
        <div className="ticker text-[color:var(--text-3)] mb-3">
          /// MANUEL BARKOD EKLE
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            data-testid="bulk-entry-manual-barkod"
            value={manualBarkod}
            onChange={(e) => setManualBarkod(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitManual();
              }
            }}
            placeholder="Barkod (ör: 00173176_088)"
            disabled={!sehpa.trim() || submitting}
            className="flex-1 h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none font-mono disabled:opacity-50"
          />
          <div className="flex items-center gap-2">
            <label className="ticker text-[color:var(--text-2)]">Adet</label>
            <input
              data-testid="bulk-entry-manual-adet"
              type="number"
              min="1"
              value={manualAdet}
              onChange={(e) => setManualAdet(Math.max(1, Number(e.target.value) || 1))}
              disabled={!sehpa.trim() || submitting}
              className="w-20 h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none font-mono text-center disabled:opacity-50"
            />
          </div>
          <button
            type="button"
            onClick={submitManual}
            data-testid="bulk-entry-manual-add"
            disabled={!sehpa.trim() || submitting || !manualBarkod.trim()}
            className="h-11 px-6 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
          >
            <Plus size={18} strokeWidth={2.5} /> Ekle
          </button>
        </div>
        <div className="text-xs text-[color:var(--text-3)] mt-2">
          Barkodu klavyeden yazın, adedi seçin ve <kbd className="px-1.5 py-0.5 bg-[color:var(--bg)] hard text-[10px] border border-[color:var(--border)]">Enter</kbd> ya da <b>Ekle</b>&apos;ye basın. Barkod bilinmiyorsa yeni ürün formu açılır.
        </div>
      </div>

      {/* Items table */}
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)]">
        <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
          <div>
            <h3 className="font-bold">Fiş Kalemleri</h3>
            <div className="ticker text-[color:var(--text-3)]">
              {rows.length} kalem · Toplam {totalAdet} adet
            </div>
          </div>
          <div className="flex gap-2">
            <button
              disabled={rows.length === 0 || submitting}
              onClick={() => setRows([])}
              data-testid="bulk-entry-clear"
              className="px-4 py-2 hard border-2 border-[color:var(--border)] hover:border-[color:var(--error)] hover:text-[color:var(--error)] disabled:opacity-40 transition-colors text-sm font-semibold"
            >
              Temizle
            </button>
            <button
              disabled={rows.length === 0 || submitting || !sehpa.trim()}
              onClick={submit}
              data-testid="bulk-entry-submit"
              className="px-6 py-2 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider disabled:opacity-40 transition-colors flex items-center gap-2"
            >
              <ArrowDownToLine size={16} strokeWidth={2.5} />
              {submitting ? "Kaydediliyor…" : "Girişi Onayla"}
            </button>
          </div>
        </div>
        <div className="overflow-auto" style={{ maxHeight: "50vh" }}>
          <table className="dense-table w-full">
            <thead>
              <tr>
                <th></th>
                <th>Barkod</th>
                <th>İsim</th>
                <th>Ebat</th>
                <th>Adet</th>
                <th>Mevcut</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-[color:var(--text-3)] py-12">
                    Fiş boş. Yukarıdan barkod okutmaya başlayın.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.barkod} className="row-enter" data-testid="bulk-entry-row">
                  <td>
                    {r.isNew ? (
                      <span className="inline-block px-1.5 py-0.5 hard bg-[color:var(--accent-bg)] text-[color:var(--accent)] text-[10px] font-bold uppercase">
                        Yeni
                      </span>
                    ) : (
                      <Check size={14} className="text-[color:var(--success)]" />
                    )}
                  </td>
                  <td className="font-mono font-semibold">{r.barkod}</td>
                  <td>
                    {isAdmin ? (
                      <input
                        data-testid="bulk-entry-row-isim"
                        value={r.isim || ""}
                        onChange={(e) => changeIsim(r.barkod, e.target.value)}
                        placeholder="İsim / tür"
                        className="w-full min-w-[160px] h-8 px-2 hard border border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none text-xs"
                      />
                    ) : (
                      <div
                        data-testid="bulk-entry-row-isim-readonly"
                        className="flex items-center gap-1 min-w-[160px] max-w-xs truncate text-xs"
                        title="Ürün ismini yalnızca yönetici değiştirebilir"
                      >
                        <Lock size={11} className="text-[color:var(--text-3)] shrink-0" />
                        <span className="truncate">{r.isim || "-"}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      data-testid="bulk-entry-row-ebat"
                      value={r.ebat || ""}
                      onChange={(e) => changeEbat(r.barkod, e.target.value)}
                      placeholder="6000X3210"
                      className="w-32 h-8 px-2 hard border border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none font-mono text-xs"
                    />
                  </td>
                  <td>
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => changeAdet(r.barkod, -1)}
                        className="w-6 h-6 hard border border-[color:var(--border)] hover:bg-[color:var(--bg)] font-mono"
                      >
                        −
                      </button>
                      <span className="font-mono font-bold px-2 min-w-[2ch] text-center">
                        {r.adet}
                      </span>
                      <button
                        onClick={() => changeAdet(r.barkod, 1)}
                        className="w-6 h-6 hard border border-[color:var(--border)] hover:bg-[color:var(--bg)] font-mono"
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td className="text-[color:var(--text-3)] text-xs font-mono">
                    {r.isNew ? "-" : `${r.currentAdet} → ${r.currentAdet + r.adet}`}
                  </td>
                  <td>
                    <button
                      onClick={() => removeRow(r.barkod)}
                      className="p-1.5 hard hover:bg-[color:var(--error-bg)] hover:text-[color:var(--error)] transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New product modal */}
      {newFormFor && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white hard border-2 border-[color:var(--border)]">
            <div className="p-4 border-b border-[color:var(--border)] flex items-center justify-between">
              <div>
                <div className="ticker text-[color:var(--text-3)]">
                  /// YENİ ÜRÜN
                </div>
                <div className="font-bold font-mono">{newFormFor}</div>
              </div>
              <button
                onClick={() => setNewFormFor(null)}
                className="p-2 hard hover:bg-[color:var(--bg)]"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="ticker block mb-1 text-[color:var(--text-2)]">
                  İsim / Tür *
                </label>
                <input
                  data-testid="new-product-isim"
                  autoFocus
                  value={newForm.isim}
                  onChange={(e) => setNewForm((f) => ({ ...f, isim: e.target.value }))}
                  placeholder="Örn: 6mm Ultra Clear"
                  className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
                />
              </div>
              <div>
                <label className="ticker block mb-1 text-[color:var(--text-2)]">
                  Ebat
                </label>
                <input
                  data-testid="new-product-ebat"
                  value={newForm.ebat}
                  onChange={(e) => setNewForm((f) => ({ ...f, ebat: e.target.value }))}
                  placeholder="6000X3210"
                  className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="ticker block mb-1 text-[color:var(--text-2)]">
                    Adet
                  </label>
                  <input
                    data-testid="new-product-adet"
                    type="number"
                    min="1"
                    value={newForm.adet}
                    onChange={(e) => setNewForm((f) => ({ ...f, adet: e.target.value }))}
                    className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="ticker block mb-1 text-[color:var(--text-2)]">
                    Kalite
                  </label>
                  <select
                    data-testid="new-product-kalite"
                    value={newForm.urun_kalitesi}
                    onChange={(e) => setNewForm((f) => ({ ...f, urun_kalitesi: e.target.value }))}
                    className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
                  >
                    <option>SAĞLAM</option>
                    <option>KIRIK VAR</option>
                    <option>ISLAK</option>
                    <option>KULLANMAYINIZ</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="ticker block mb-1 text-[color:var(--text-2)]">
                  Açıklama
                </label>
                <input
                  data-testid="new-product-aciklama"
                  value={newForm.aciklama}
                  onChange={(e) => setNewForm((f) => ({ ...f, aciklama: e.target.value }))}
                  className="w-full h-11 px-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setNewFormFor(null)}
                  className="flex-1 h-11 hard border-2 border-[color:var(--border)] hover:bg-[color:var(--bg)] font-semibold"
                >
                  İptal
                </button>
                <button
                  data-testid="new-product-save"
                  onClick={addNewProduct}
                  className="flex-1 h-11 hard bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2"
                >
                  <Plus size={16} /> Ekle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CameraScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={(code) => handleScan(code)}
      />

      <LabelPhotoScanner
        open={labelScannerOpen}
        onClose={() => setLabelScannerOpen(false)}
        onExtract={handleLabelExtract}
      />
    </div>
  );
}
